import { useConst } from '@fluentui/react-hooks'
import * as UUID from 'uuid'
import { ReactElement } from 'react'
import useSubmitDocumentGeneration from '../../document/useSubmitDocumentGeneration'
import { QueueCallbacks } from '../../queue/queueTypes'
import { DocumentGenerationContextData, DocumentGenerationType } from '../../document/documentGenerationTypes'

/**
 * This is the form submission handler when we want to generate a report of some kind. It's the point of entry that
 * first creates the toast message.
 */
export default function useSubmitDocumentGenerationRequest<RequestT, StatusT = {}>(
    generationType: DocumentGenerationType,
    hookCallbacks?: QueueCallbacks<RequestT, StatusT, DocumentGenerationContextData>,
    allowMultiple?: boolean,
) {
    const componentRequestId = useConst(() => UUID.v4())
    const submit = useSubmitDocumentGeneration<RequestT, StatusT>(generationType)

    return (
        data: Omit<RequestT, 'generationType'>,
        title?: ReactElement | string,
        dispatchCallbacks?: QueueCallbacks<RequestT, StatusT, DocumentGenerationContextData>,
        // Pass this when the screen needs to recognise the job later, for instance to disable the button
        // that started it. Without one, every generation from this component shares a single id.
        requestId?: string,
    ) => {
        submit({
            requestId: requestId || (allowMultiple ? UUID.v4() : componentRequestId),
            data: { ...data, generationType } as RequestT,
            title,
            callbacks: {
                ...(hookCallbacks || {}),
                ...(dispatchCallbacks || {}),
            },
        })
    }
}
